import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { History, Search, Download, Loader2, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const ACTION_BADGE_COLORS = {
  'shift.create': 'bg-emerald-100 text-emerald-700',
  'shift.update': 'bg-amber-100 text-amber-700',
  'shift.delete': 'bg-rose-100 text-rose-700',
  'staff_access.update': 'bg-purple-100 text-purple-700',
  'audit_log.delete': 'bg-slate-100 text-slate-700',
};

export default function AuditLogTab({ api }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');
  const [filterTarget, setFilterTarget] = useState('all');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [search, setSearch] = useState('');
  const [knownActions, setKnownActions] = useState([]);
  const [knownTargetTypes, setKnownTargetTypes] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterAction !== 'all') params.set('action', filterAction);
      if (filterTarget !== 'all') params.set('target_type', filterTarget);
      if (start) params.set('start', start);
      if (end) params.set('end', end);
      params.set('limit', '500');
      const [logResp, actionsResp] = await Promise.all([
        api.get(`/audit-log?${params.toString()}`),
        api.get('/audit-log/actions'),
      ]);
      setEntries(logResp.data.entries || []);
      setKnownActions(actionsResp.data.actions || []);
      setKnownTargetTypes(actionsResp.data.target_types || []);
    } catch {
      toast.error('Failed to load audit log');
    }
    setLoading(false);
  }, [api, filterAction, filterTarget, start, end]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(e =>
      (e.actor_email || '').toLowerCase().includes(q) ||
      (e.actor_name || '').toLowerCase().includes(q) ||
      (e.action || '').toLowerCase().includes(q) ||
      (e.target_label || '').toLowerCase().includes(q));
  }, [entries, search]);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filterAction !== 'all') params.set('action', filterAction);
      if (filterTarget !== 'all') params.set('target_type', filterTarget);
      if (start) params.set('start', start);
      if (end) params.set('end', end);
      const resp = await api.get(`/audit-log/export?${params.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="audit-log-tab">
      <Card className="border-[#DED8EB] shadow-sm">
        <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <History className="h-4 w-4 text-[#6B3FA0]" /> Audit Log
            </CardTitle>
            <p className="text-sm text-[#5A4D6E]">Every shift edit, deletion, and module access change. Auto-purges after 2 years (or delete entries manually).</p>
          </div>
          <Button onClick={exportCsv} size="sm" variant="outline" className="border-[#DED8EB] gap-1.5 h-8" data-testid="audit-export-btn">
            <Download className="h-3 w-3" /> Export CSV
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <div className="relative md:col-span-2">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-[#8E85A0]" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search actor or target…" className="pl-7 border-[#DED8EB] h-8 text-sm" data-testid="audit-search" />
            </div>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="border-[#DED8EB] h-8 text-sm" data-testid="audit-action-filter"><SelectValue placeholder="Action" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {knownActions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterTarget} onValueChange={setFilterTarget}>
              <SelectTrigger className="border-[#DED8EB] h-8 text-sm" data-testid="audit-target-filter"><SelectValue placeholder="Target type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All targets</SelectItem>
                {knownTargetTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 text-[#8E85A0] shrink-0" />
              <Input type="date" value={start} onChange={e => setStart(e.target.value)} className="h-8 text-xs border-[#DED8EB]" data-testid="audit-start-date" />
              <Input type="date" value={end} onChange={e => setEnd(e.target.value)} className="h-8 text-xs border-[#DED8EB]" data-testid="audit-end-date" />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-[#8E85A0]">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-[#8E85A0] py-12 text-center">No audit entries match your filters.</p>
          ) : (
            <div className="rounded-lg border border-[#DED8EB] overflow-hidden">
              <table className="w-full text-sm" data-testid="audit-log-table">
                <thead className="bg-[#F7F4EF] text-[10px] uppercase tracking-wider text-[#5A4D6E]">
                  <tr>
                    <th className="text-left p-2 font-semibold">When</th>
                    <th className="text-left p-2 font-semibold">Who</th>
                    <th className="text-left p-2 font-semibold">Action</th>
                    <th className="text-left p-2 font-semibold">Target</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#DED8EB]">
                  {filtered.map(e => (
                    <tr key={e.id} className="hover:bg-[#F7F4EF]/50">
                      <td className="p-2 whitespace-nowrap text-[11px] text-[#5A4D6E]">{formatTimestamp(e.created_at)}</td>
                      <td className="p-2">
                        <div className="font-semibold text-[#2D1B4E]">{e.actor_name || '—'}</div>
                        <div className="text-[11px] text-[#8E85A0]">{e.actor_email}</div>
                      </td>
                      <td className="p-2">
                        <Badge className={`text-[10px] border-0 ${ACTION_BADGE_COLORS[e.action] || 'bg-slate-100 text-slate-700'}`}>{e.action}</Badge>
                      </td>
                      <td className="p-2 text-[#5A4D6E]">{e.target_label || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[10px] text-[#8E85A0] text-right">Showing {filtered.length} entries (max 500 — narrow filters or export to see more).</p>
        </CardContent>
      </Card>
    </div>
  );
}

function formatTimestamp(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
}
